import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DataTableEnhanced, type ColumnSort, type ColumnDef } from "@/components/ui/data-table-enhanced";
import { PaginationControls } from "@/components/pagination-controls";
import { calculatePagination, paginateArray, type PaginationMetadata } from "@/types/pagination";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Receipt,
  FileText,
  ArrowRight,
  Search,
  X,
  CalendarDays,
  TrendingUp,
  Settings2,
} from "lucide-react";
import type { BillingMilestone, BillingMilestoneStatus, BillingTriggerType } from "@/types/budget.types";
import { InvoicePdfActions } from "@/components/invoices";
import type { InvoicePdfTransaction } from "@/types/invoice-pdf";

// ============================================================================
// Types
// ============================================================================

interface Invoice {
  id: number;
  invoiceNumber: string | null;
  date: string | null;
  description: string;
  amount: number;
  due: string | null;
  status: "Draft" | "Sent" | "Paid" | "Overdue";
  /** Optional: ID of the milestone this invoice was raised against. When present,
   *  matching uses ID instead of amount so milestones with equal amounts don't
   *  incorrectly inherit each other's paid status. */
  milestoneId?: number;
}

interface FixedFeeBillingTabProps {
  /** Contract value from the Sales Budget */
  contractValue: number;
  /** Billing milestones from the Sales Budget */
  milestones: BillingMilestone[];
  /** Invoices already raised for this project */
  invoices: Invoice[];
  /** Project name — used in wizard header */
  projectName: string;
  /** Project completion % (0–100) — used to evaluate percent-complete milestone status */
  completionPercent: number;
  /**
   * Project-level billing trigger type (Phase 7+).
   * Determines how ALL milestones on this project are evaluated.
   * Defaults to 'date' for backwards compatibility.
   */
  billingTriggerType?: BillingTriggerType;
  /** Called when user clicks "Generate Invoice" on a milestone row */
  onGenerateInvoice: (milestone: BillingMilestone) => void;
  /** Called when user clicks "Billing Settings" */
  onSettingsClick?: () => void;
  /** Currency formatter */
  formatCurrency: (value: number) => string;
  /** Pre-set the invoice history status filter (e.g. when drilling through from Financials tab) */
  initialStatusFilter?: string;
  /** Whether the Default Cost Centre has been configured in Billing Settings */
  costCentreConfigured?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Match an invoice to a milestone.
 *
 * 1. If the invoice has a `milestoneId`, match by ID (exact).
 * 2. Otherwise fall back to amount + description matching to avoid false
 *    positives when multiple milestones share the same amount.
 *    The invoice description is set to the milestone name during creation.
 */
function invoiceMatchesMilestone(inv: Invoice, milestone: BillingMilestone): boolean {
  if (inv.milestoneId != null) {
    return inv.milestoneId === milestone.id;
  }
  // Amount must match.
  if (Math.abs(inv.amount - milestone.amount) >= 0.01) return false;
  // When a description is available, require it to match the milestone name
  // so that one invoice does not falsely match every equal-amount milestone.
  if (inv.description && milestone.name) {
    return inv.description.trim().toLowerCase() === milestone.name.trim().toLowerCase();
  }
  // No description to disambiguate — accept the amount match.
  return true;
}

/**
 * Derive the display status for a milestone row.
 *
 * For `date` milestones the logic is unchanged — compare dueDate to today.
 * For `percent-complete` milestones:
 *   - `paid`      — matching Paid invoice exists
 *   - `due`       — matching Sent invoice exists (already invoiced)
 *   - `due`       — threshold met (completionPercent >= triggerPercent), not yet invoiced
 *   - `overdue`   — threshold met AND optional deadline date has passed; OR
 *                   threshold NOT met but deadline date has passed
 *   - `pending`   — threshold not yet reached and no deadline passed
 */
function getMilestoneDisplayStatus(
  milestone: BillingMilestone,
  invoices: Invoice[],
  completionPercent: number,
  billingTriggerType: BillingTriggerType = 'date'
): BillingMilestoneStatus {
  const invoicesForMilestone = invoices.filter((inv) =>
    invoiceMatchesMilestone(inv, milestone)
  );

  const hasMatchingInvoice = invoicesForMilestone.some(
    (inv) => inv.status === "Sent" || inv.status === "Paid"
  );

  if (hasMatchingInvoice) {
    const paidInvoice = invoicesForMilestone.find((inv) => inv.status === "Paid");
    return paidInvoice ? "paid" : "due";
  }

  // — % Complete milestone logic — driven by project-level billingTriggerType (Phase 7+)
  if (billingTriggerType === "percent-complete") {
    const thresholdMet =
      milestone.triggerPercent !== undefined &&
      completionPercent >= milestone.triggerPercent;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlinePassed =
      milestone.dueDate
        ? new Date(milestone.dueDate) < today
        : false;

    if (thresholdMet && deadlinePassed) return "overdue"; // Met but deadline blown
    if (!thresholdMet && deadlinePassed) return "overdue"; // Deadline passed before reaching threshold
    if (thresholdMet) return "due";                         // Ready to invoice
    return "pending";                                        // Not yet reached
  }

  // — Date milestone logic — re-evaluate against today's date so that
  // milestones whose due date has passed show as "due" (actionable) even
  // when the raw status from the data source is still "pending".
  if (milestone.dueDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(milestone.dueDate);
    due.setHours(0, 0, 0, 0);
    if (due <= today) {
      return "due";
    }
  }
  return milestone.status;
}

/**
 * Returns the status badge for a milestone row.
 *
 * `hasMatchingInvoice` is used to disambiguate "due" which can mean either
 * "has a Sent invoice" OR "threshold met, not yet invoiced". Only the former
 * should show "Invoiced" — the latter shows "Due" to prompt action.
 */
function getMilestoneStatusBadge(
  status: BillingMilestoneStatus,
  hasMatchingInvoice = false,
) {
  switch (status) {
    case "paid":
      return (
        <Badge variant="success" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          Paid
        </Badge>
      );
    case "due":
      // "Invoiced" when a Sent invoice exists; "Due" when threshold met but not yet invoiced
      return hasMatchingInvoice ? (
        <Badge variant="default" className="gap-1">
          <FileText className="h-3 w-3" />
          Invoiced
        </Badge>
      ) : (
        <Badge variant="warning" className="gap-1">
          <Clock className="h-3 w-3" />
          Due
        </Badge>
      );
    case "overdue":
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Overdue
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          Scheduled
        </Badge>
      );
    case "future":
      return (
        <Badge variant="outline" className="gap-1">
          <CalendarDays className="h-3 w-3" />
          Future
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function getInvoiceStatusBadge(status: Invoice["status"]) {
  switch (status) {
    case "Paid":
      return <Badge variant="success">Paid</Badge>;
    case "Sent":
      return <Badge variant="default">Sent</Badge>;
    case "Overdue":
      return <Badge variant="destructive">Overdue</Badge>;
    case "Draft":
      return <Badge variant="secondary">Draft</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ============================================================================
// Summary Bar
// ============================================================================

interface SummaryBarProps {
  contractValue: number;
  invoicedToDate: number;
  outstanding: number;
  nextBillingEvent: BillingMilestone | null;
  formatCurrency: (v: number) => string;
}

function SummaryBar({
  contractValue,
  invoicedToDate,
  outstanding,
  nextBillingEvent,
  formatCurrency,
}: SummaryBarProps) {
  // Determine overall status
  const overallStatus = useMemo(() => {
    if (outstanding <= 0) return "complete";
    if (
      nextBillingEvent &&
      (nextBillingEvent.status === "overdue")
    ) {
      return "overdue";
    }
    return "on-track";
  }, [outstanding, nextBillingEvent]);

  const statusBadge = useMemo(() => {
    switch (overallStatus) {
      case "complete":
        return (
          <Badge variant="success" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            Complete
          </Badge>
        );
      case "overdue":
        return (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Overdue
          </Badge>
        );
      default:
        return (
          <Badge variant="default" className="gap-1">
            <TrendingUp className="h-3 w-3" />
            On Track
          </Badge>
        );
    }
  }, [overallStatus]);

  return (
    <div className="flex flex-wrap items-center gap-6 px-5 py-4 bg-neutral-50 rounded-lg border border-neutral-200">
      {/* Contract Value */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Contract Value
        </span>
        <span className="text-base font-semibold text-neutral-900">
          {formatCurrency(contractValue)}
        </span>
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Invoiced to Date */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Invoiced to Date
        </span>
        <span className="text-base font-semibold text-neutral-900">
          {formatCurrency(invoicedToDate)}
        </span>
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Outstanding */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Outstanding
        </span>
        <span
          className={`text-base font-semibold ${
            outstanding > 0 ? "text-warning-600" : "text-success-600"
          }`}
        >
          {formatCurrency(outstanding)}
        </span>
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Next Billing Event */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Next Billing Event
        </span>
        {nextBillingEvent ? (
          <div className="flex flex-col">
            <span className="text-base font-semibold text-neutral-900">
              {nextBillingEvent.name}
            </span>
            <span className="text-xs text-muted-foreground mt-0.5">
              {nextBillingEvent.triggerPercent !== undefined
                ? `At ${nextBillingEvent.triggerPercent}% complete`
                : formatDate(nextBillingEvent.dueDate)}
            </span>
          </div>
        ) : (
          <span className="text-base font-semibold text-muted-foreground">None</span>
        )}
      </div>

      <div className="h-8 w-px bg-border hidden sm:block" />

      {/* Status */}
      <div className="flex flex-col">
        <span className="text-xs text-muted-foreground uppercase tracking-wide mb-0.5">
          Status
        </span>
        <div className="mt-0.5">{statusBadge}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Billing Schedule Table
// ============================================================================

interface BillingScheduleProps {
  milestones: BillingMilestone[];
  invoices: Invoice[];
  completionPercent: number;
  billingTriggerType?: BillingTriggerType;
  onGenerateInvoice: (milestone: BillingMilestone) => void;
  formatCurrency: (v: number) => string;
  costCentreConfigured?: boolean;
}

function BillingScheduleTable({
  milestones,
  invoices,
  completionPercent,
  billingTriggerType = 'date',
  onGenerateInvoice,
  formatCurrency,
  costCentreConfigured = true,
}: BillingScheduleProps) {
  const rows = useMemo(
    () =>
      milestones.map((m) => {
        const invoicesForMilestone = invoices.filter((inv) =>
          invoiceMatchesMilestone(inv, m),
        );
        const hasMatchingInvoice = invoicesForMilestone.some(
          (inv) => inv.status === "Sent" || inv.status === "Paid",
        );
        return {
          ...m,
          displayStatus: getMilestoneDisplayStatus(m, invoices, completionPercent, billingTriggerType),
          hasMatchingInvoice,
        };
      }),
    [milestones, invoices, completionPercent, billingTriggerType]
  );

  // "Generate Invoice" is only shown when a milestone needs action — i.e. it
  // hasn't been invoiced yet. "due" can mean threshold met OR already invoiced;
  // the hasMatchingInvoice flag on each row differentiates these two cases.
  const isActionable = (status: BillingMilestoneStatus, hasInvoice: boolean) =>
    !hasInvoice && (status === "due" || status === "overdue");

  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const sortedRows = useMemo(() => {
    if (columnSorts.length === 0) return rows;
    return [...rows].sort((a, b) => {
      for (const sort of columnSorts) {
        let cmp = 0;
        switch (sort.columnId) {
          case "name":        cmp = a.name.localeCompare(b.name); break;
          case "amount":      cmp = a.amount - b.amount; break;
          case "displayStatus": cmp = a.displayStatus.localeCompare(b.displayStatus); break;
          case "trigger":
            cmp = (a.dueDate ?? "").localeCompare(b.dueDate ?? ""); break;
        }
        if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });
  }, [rows, columnSorts]);

  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(sortedRows.length, currentPage, itemsPerPage),
    [sortedRows.length, currentPage, itemsPerPage]
  );
  const paginatedRows = useMemo(
    () => paginateArray(sortedRows, currentPage, itemsPerPage).data,
    [sortedRows, currentPage, itemsPerPage]
  );

  type MilestoneRow = (typeof rows)[number];

  const columns: ColumnDef<MilestoneRow, unknown>[] = [
    {
      id: "name",
      accessorKey: "name",
      header: "Milestone",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      id: "trigger",
      accessorKey: "dueDate",
      header: "Trigger",
      cell: ({ row }) => {
        const r = row.original;
        const isPercent = billingTriggerType === "percent-complete";
        const thresholdMet =
          isPercent && r.triggerPercent !== undefined && completionPercent >= r.triggerPercent;
        return isPercent ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <span
                className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${
                  thresholdMet ? "bg-success-500" : "bg-neutral-300"
                }`}
              />
              <span className="text-sm text-foreground">
                At {r.triggerPercent}%
                <span className={`ml-1 text-xs font-medium ${thresholdMet ? "text-success-600" : "text-neutral-500"}`}>
                  · project at {completionPercent}%
                </span>
              </span>
            </div>
            {r.dueDate && (
              <div className="text-xs text-muted-foreground pl-3.5">
                Deadline: {formatDate(r.dueDate)}
              </div>
            )}
          </div>
        ) : (
          <span className="text-sm text-foreground">{formatDate(r.dueDate)}</span>
        );
      },
    },
    {
      id: "amount",
      accessorKey: "amount",
      header: () => <div className="text-right">Amount</div>,
      cell: ({ row }) => (
        <div className="text-right font-medium">{formatCurrency(row.original.amount)}</div>
      ),
    },
    {
      id: "displayStatus",
      accessorKey: "displayStatus",
      header: "Status",
      cell: ({ row }) =>
        getMilestoneStatusBadge(row.original.displayStatus, row.original.hasMatchingInvoice),
    },
    {
      id: "action",
      header: () => <div className="text-right">Action</div>,
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        return isActionable(r.displayStatus, r.hasMatchingInvoice) ? (
          <div className="flex justify-end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={!costCentreConfigured ? 0 : undefined}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onGenerateInvoice(r)}
                      className="gap-1"
                      disabled={!costCentreConfigured}
                      style={!costCentreConfigured ? { pointerEvents: "none" } : undefined}
                    >
                      <Receipt className="h-3.5 w-3.5" />
                      Generate Invoice
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {!costCentreConfigured
                    ? "Set a Default Cost Centre in Billing Settings before generating an invoice"
                    : "Create an invoice for this billing milestone"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <div className="flex justify-end">
            <span className="text-xs text-muted-foreground">—</span>
          </div>
        );
      },
    },
  ];

  return (
    <DataTableEnhanced
      columns={columns}
      data={paginatedRows}
      columnSorts={columnSorts}
      onColumnSortChange={(newSorts) => {
        setColumnSorts(newSorts);
        setCurrentPage(1);
      }}
      emptyMessage={
        milestones.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <CalendarDays className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No billing milestones defined.</p>
            <p className="text-xs mt-1">Add milestones in the Sales Budget to build a billing schedule.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
            <CalendarDays className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No milestones match your filters.</p>
          </div>
        )
      }
      paginationContent={
        pagination.totalPages > 1 ? (
          <PaginationControls
            pagination={pagination}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
            showPageSizeSelector
          />
        ) : undefined
      }
    />
  );
}

// ============================================================================
// Invoice History Table (reused from T&M pattern)
// ============================================================================

interface InvoiceHistoryProps {
  invoices: Invoice[];
  formatCurrency: (v: number) => string;
  initialStatusFilter?: string;
}

function InvoiceHistoryTable({ invoices, formatCurrency, initialStatusFilter = "all" }: InvoiceHistoryProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sync if parent changes the initial filter (e.g. drill-through fired while already on Billing tab)
  React.useEffect(() => {
    setStatusFilter(initialStatusFilter);
    setCurrentPage(1);
  }, [initialStatusFilter]);

  const filtered = useMemo(
    () =>
      invoices.filter((inv) => {
        const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
        const q = search.toLowerCase();
        const matchesSearch =
          !q ||
          inv.invoiceNumber?.toLowerCase().includes(q) ||
          inv.description.toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
      }),
    [invoices, search, statusFilter]
  );

  const sortedFiltered = useMemo(() => {
    if (columnSorts.length === 0) return filtered;
    return [...filtered].sort((a, b) => {
      for (const sort of columnSorts) {
        let cmp = 0;
        switch (sort.columnId) {
          case "invoiceNumber": cmp = (a.invoiceNumber ?? "").localeCompare(b.invoiceNumber ?? ""); break;
          case "date":          cmp = (a.date ?? "").localeCompare(b.date ?? ""); break;
          case "description":   cmp = a.description.localeCompare(b.description); break;
          case "amount":        cmp = a.amount - b.amount; break;
          case "due":           cmp = (a.due ?? "").localeCompare(b.due ?? ""); break;
          case "status":        cmp = a.status.localeCompare(b.status); break;
        }
        if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
      }
      return 0;
    });
  }, [filtered, columnSorts]);

  const totals = useMemo(() => {
    const invoicedTotal = invoices.filter((i) => i.status !== "Draft").reduce((s, i) => s + i.amount, 0);
    const paidTotal = invoices.filter((i) => i.status === "Paid").reduce((s, i) => s + i.amount, 0);
    const outstandingTotal = invoices.filter((i) => i.status === "Sent" || i.status === "Overdue").reduce((s, i) => s + i.amount, 0);
    return { invoicedTotal, paidTotal, outstandingTotal };
  }, [invoices]);

  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(sortedFiltered.length, currentPage, itemsPerPage),
    [sortedFiltered.length, currentPage, itemsPerPage]
  );
  const paginatedInvoices = useMemo(
    () => paginateArray(sortedFiltered, currentPage, itemsPerPage).data,
    [sortedFiltered, currentPage, itemsPerPage]
  );

  const columns: ColumnDef<Invoice, unknown>[] = [
    {
      id: "invoiceNumber",
      accessorKey: "invoiceNumber",
      header: "Invoice #",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.invoiceNumber ?? `#${row.original.id}`}</span>
      ),
    },
    {
      id: "date",
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => formatDate(row.original.date),
    },
    {
      id: "description",
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => row.original.description,
    },
    {
      id: "amount",
      accessorKey: "amount",
      header: () => <div className="text-right">Amount</div>,
      cell: ({ row }) => (
        <div className="text-right font-medium">{formatCurrency(row.original.amount)}</div>
      ),
    },
    {
      id: "due",
      accessorKey: "due",
      header: "Due",
      cell: ({ row }) => formatDate(row.original.due),
    },
    {
      id: "daysOverdue",
      header: () => <div className="text-right">Days Overdue</div>,
      enableSorting: false,
      cell: ({ row }) => {
        const inv = row.original;
        if (inv.status !== "Overdue" || !inv.due) return <div className="text-right text-muted-foreground">—</div>;
        const days = Math.floor((Date.now() - new Date(inv.due).getTime()) / 86_400_000);
        const bucket = days > 90 ? "90+ days" : days > 60 ? "61–90 days" : days > 30 ? "31–60 days" : "1–30 days";
        const colour = days > 60 ? "text-error-600" : days > 30 ? "text-warning-700" : "text-warning-600";
        return (
          <div className={`text-right font-medium ${colour}`} title={`${days} days overdue`}>
            {bucket}
          </div>
        );
      },
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getInvoiceStatusBadge(row.original.status),
    },
    {
      id: "pdf",
      header: "PDF",
      enableSorting: false,
      cell: ({ row }) => {
        const inv = row.original;
        const pdfTransaction: InvoicePdfTransaction = {
          invoiceNumber: inv.invoiceNumber ?? `#${inv.id}`,
          date: inv.date ?? new Date().toISOString(),
          dueDate: inv.due ?? undefined,
          status: inv.status,
          description: inv.description,
          amount: inv.amount,
        };
        return <InvoicePdfActions invoice={pdfTransaction} />;
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invoice Documents</CardTitle>
        <CardDescription>All invoices raised against this project</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Totals strip */}
        {invoices.length > 0 && (
          <div className="flex flex-wrap gap-6 p-3 bg-neutral-50 rounded-md border border-neutral-200 text-sm">
            <div>
              <span className="text-muted-foreground">Total Invoiced:</span>
              <span className="ml-2 font-semibold">{formatCurrency(totals.invoicedTotal)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Paid:</span>
              <span className="ml-2 font-semibold text-success-600">{formatCurrency(totals.paidTotal)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Outstanding:</span>
              <span className="ml-2 font-semibold text-warning-600">{formatCurrency(totals.outstandingTotal)}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="pl-9 pr-9"
            />
            {search && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                onClick={() => { setSearch(""); setCurrentPage(1); }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Sent">Sent</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <DataTableEnhanced
          columns={columns}
          data={paginatedInvoices}
          columnSorts={columnSorts}
          onColumnSortChange={(newSorts) => {
            setColumnSorts(newSorts);
            setCurrentPage(1);
          }}
          emptyMessage={
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <Receipt className="h-10 w-10 mb-3 opacity-40" />
              <p className="text-sm">
                {invoices.length === 0
                  ? "No invoices have been raised yet."
                  : "No invoices match your filters."}
              </p>
            </div>
          }
          paginationContent={
            pagination.totalPages > 1 ? (
              <PaginationControls
                pagination={pagination}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                showPageSizeSelector
              />
            ) : undefined
          }
        />
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main Component — FixedFeeBillingTab
// ============================================================================

export function FixedFeeBillingTab({
  contractValue,
  milestones,
  invoices,
  projectName: _projectName,
  completionPercent,
  billingTriggerType = 'date',
  onGenerateInvoice,
  onSettingsClick,
  formatCurrency,
  costCentreConfigured = true,
  initialStatusFilter = "all",
}: FixedFeeBillingTabProps) {
  // Derived summary values
  const invoicedToDate = useMemo(
    () =>
      invoices
        .filter((i) => i.status === "Sent" || i.status === "Paid")
        .reduce((s, i) => s + i.amount, 0),
    [invoices]
  );

  const outstanding = useMemo(
    () => Math.max(0, contractValue - invoicedToDate),
    [contractValue, invoicedToDate]
  );

  const nextBillingEvent = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Re-derive live status for all milestones using completionPercent and project-level trigger type
    const withStatus = milestones.map((m) => ({
      ...m,
      liveStatus: getMilestoneDisplayStatus(m, invoices, completionPercent, billingTriggerType),
    }));

    // Exclude already-paid or invoiced milestones
    const actionable = withStatus.filter(
      (m) => m.liveStatus === "pending" || m.liveStatus === "due" || m.liveStatus === "overdue"
    );

    // Prefer overdue milestones first
    const overdue = actionable.filter((m) => m.liveStatus === "overdue");
    if (overdue.length > 0) return overdue[0];

    // For % Complete milestones that are "due", surface the one with the lowest threshold
    const duePct = actionable
      .filter((m) => m.liveStatus === "due" && billingTriggerType === "percent-complete")
      .sort((a, b) => (a.triggerPercent ?? 0) - (b.triggerPercent ?? 0));
    if (duePct.length > 0) return duePct[0];

    // For date milestones, return the next upcoming one
    const futureDate = actionable
      .filter((m) => billingTriggerType === "date" && m.dueDate)
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .filter((m) => new Date(m.dueDate).getTime() >= today.getTime());
    if (futureDate.length > 0) return futureDate[0];

    // Fall back to any pending % Complete milestone (lowest threshold)
    const pendingPct = actionable
      .filter(() => billingTriggerType === "percent-complete")
      .sort((a, b) => (a.triggerPercent ?? 0) - (b.triggerPercent ?? 0));
    return pendingPct[0] ?? null;
  }, [milestones, invoices, completionPercent, billingTriggerType]);

  return (
    <div className="space-y-6">
      {/* 3.1 — Summary Bar */}
      <SummaryBar
        contractValue={contractValue}
        invoicedToDate={invoicedToDate}
        outstanding={outstanding}
        nextBillingEvent={nextBillingEvent}
        formatCurrency={formatCurrency}
      />

      {/* 3.2 — Billing Schedule */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Billing Schedule</CardTitle>
              <CardDescription>
                Milestones from the Sales Budget. Generate an invoice directly from
                any actionable row.
              </CardDescription>
            </div>
            {onSettingsClick && (
              <Button variant="outline" size="sm" onClick={onSettingsClick}>
                <Settings2 className="h-4 w-4 mr-2" />
                Billing Settings
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <BillingScheduleTable
            milestones={milestones}
            invoices={invoices}
            completionPercent={completionPercent}
            billingTriggerType={billingTriggerType}
            onGenerateInvoice={onGenerateInvoice}
            formatCurrency={formatCurrency}
            costCentreConfigured={costCentreConfigured}
          />
        </CardContent>
      </Card>

      {/* 3.3 — Invoice History */}
      <InvoiceHistoryTable invoices={invoices} formatCurrency={formatCurrency} initialStatusFilter={initialStatusFilter} />
    </div>
  );
}
